import { Route, Routes, useParams, useLocation, Link } from 'react-router-dom'
import { gql } from 'graphql-request'
import { Pencil } from 'lucide-react'
import {
  ChartScatter,
  Lightbulb,
  X,
  CornerUpLeft
} from 'lucide-react'

import { useQuery, useMutation } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import Breadcrumbs from '@components/Breadcrumbs'
import NavTabs from '@components/NavTabs'
import { Button } from '@components/ui/button'

import SubmissionList from './SubmissionList'
import Insights from './Insights'

const SERIES_QUERY = gql`
  query series($id: ID!) {
    node(id: $id) {
      ... on Series {
        id
        name
        totalPossibleScore
        isOwner
        educatorProjects {
          id
          name
          playground
          position
          totalPossibleScore
        }
      }
    }
  }
`

const CLASSROOM_QUERY = gql`
  query classroom($id: ID!) {
    classroom(id: $id) {
      id
      name
      shortCode
      educator {
        id
        fullName
      }
    }
  }
`

const ASSIGNMENTS_QUERY = gql`
  query series($id: ID!) {
    node(id: $id) {
      ... on Series {
        assignments {
          id
          createdAt
          closed
          classroom {
            id
            name
          }
        }
      }
    }
  }
`

const UPDATE_ASSIGNMENT_MUTATION = gql`
  mutation updateAssignment($input: UpdateAssignmentInput!) {
    updateAssignment(input: $input) {
      assignment {
        id
      }
    }
  }
`

const CourseActivity = () => {
  const { courseId, classroomId } = useParams()
  const { currentMembership: { organization: { features } } } = useCurrentUser()
  const insightsEnabled = features.includes('course_insights')
  const location = useLocation()

  const { data: { node: { name, isOwner, educatorProjects = [], classrooms = [], totalPossibleScore } = {} } = {}, isLoading: isSeriesLoading } = useQuery({
    queryKey: ['seriesDetails', courseId],
    gqlQuery: SERIES_QUERY,
    variables: { id: courseId }
  })

  const { data: { classroom = {} } = {} } = useQuery({
    queryKey: ['seriesClassroom', classroomId, courseId],
    gqlQuery: CLASSROOM_QUERY,
    variables: { id: classroomId }
  })

  const { data: { node: { assignments = [] } = {} } = {}, refetch: refetchAssignments } = useQuery({
    queryKey: ['seriesAssignments', courseId],
    gqlQuery: ASSIGNMENTS_QUERY,
    variables: { id: courseId }
  })

  const { mutate: updateAssignment } = useMutation({
    gqlMutation: UPDATE_ASSIGNMENT_MUTATION,
    onSuccess: () => refetchAssignments()
  })

  const assignment = assignments.find(assignment => assignment.classroom.id === classroomId)

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Classrooms' to='/educators/classrooms' />
        <Breadcrumbs.Link label={classroom.name} to={`/educators/classrooms/${classroomId}/courses`} />
        <Breadcrumbs.Text label={name} />

        <If condition={isOwner}>
          <Link to={`/educators/courses/${courseId}/settings`} className='flex items-center rounded-full hover:bg-muted p-2'>
            <Pencil size={14} />
          </Link>
        </If>

        <div className='ml-auto'>
          <If condition={assignment}>
            <Choose>
              <When condition={assignment.closed}>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => updateAssignment({ input: { assignmentId: assignment.id, closed: false } })}
                >
                  <CornerUpLeft size={14} />
                  Reopen
                </Button>
              </When>

              <Otherwise>
                <Button
                  className='text-destructive hover:text-destructive'
                  type='button'
                  variant='outline'
                  onClick={() => updateAssignment({ input: { assignmentId: assignment.id, closed: true } })}
                >
                  <X size={14} />
                  Close
                </Button>
              </Otherwise>
            </Choose>
          </If>
        </div>
      </Breadcrumbs>

      <div className='flex flex-col mx-5'>
        <h1 className='text-2xl font-bold py-4'>{name}</h1>

        <NavTabs>
          <NavTabs.Link
            to={{ pathname: '', search: location.search }}
            end
            label='Submissions'
            icon={<ChartScatter size={16} />}
          />

          <If condition={insightsEnabled}>
            <NavTabs.Link
              to={{ pathname: 'insights', search: location.search }}
              label='Insights'
              icon={<Lightbulb size={16} />}
            />
          </If>
        </NavTabs>

        <Routes>
          <Route
            index
            element={
              <SubmissionList
                id={courseId}
                classroomId={classroomId}
                educatorProjects={educatorProjects}
                totalPossibleScore={totalPossibleScore}
              />
            }
          />

          <If condition={insightsEnabled}>
            <Route
              path='insights'
              element={
                <Insights
                  id={courseId}
                  classroomId={classroomId}
                  educatorProjects={educatorProjects}
                />
              }
            />
          </If>
        </Routes>
      </div>
    </>
  )
}

export default CourseActivity
